import React, { useState, useMemo, useEffect } from 'react';
import { useTable, useSortBy, useFilters } from 'react-table';
import { Link } from 'react-router-dom';
import EventForm from './EventForm';
import { DATA_URL } from '../config';
import { Modal, Box, Typography, Button, IconButton, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/EventBusyRounded';
import EventAvailableIcon from '@mui/icons-material/EventAvailableRounded';
import EditCalendarIcon from '@mui/icons-material/EditCalendarRounded';

const ModalStyles = {
  overlay: {
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)', position: 'fixed',
    top: 0, left: 0, width: '100%', height: '100%', zIndex: 1000,
  },
  modal: {
    position: 'relative', width: '90%', maxHeight: '92%',
    maxWidth: '1000px', overflow: 'auto', borderRadius: '0px',
    padding: '20px', backgroundColor: '#f8f8f8'
  },
  sectionHeader: {
    backgroundColor: 'var(--color-theme)', color: 'white', padding: '10px',
    marginTop: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  sectionContent: { marginBottom: '12px', padding: '0px', overflow: 'auto', userSelect: 'none' }
};

const Events = ({ user, view, isOpen, onClose, loading, events, setEvents, fetchEvents }) => {
  const [formOpen, setFormOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);

  const handleDelete = async (eventId) => {
    if (!window.confirm('Are you sure you want to cancel this event?')) return;

    try {
      const res = await fetch(`${DATA_URL}editEvent/${eventId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: 'Cancelled' })
      });
      if (!res.ok) throw new Error('Failed to update event');
      const updatedEvent = await res.json();
      setEvents(prev => prev.map(ev => ev.id === updatedEvent.id ? updatedEvent : ev));
    } catch (err) {
      console.error('Error deleting event:', err);
    }
  };

  const handleApprove = async (eventId) => {
    if (!window.confirm('Approve Network Event?')) return;

    try {
      const res = await fetch(`${DATA_URL}editEvent/${eventId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: 'Confirmed' })
      });
      if (!res.ok) throw new Error('Failed to update event');
      const updatedEvent = await res.json();
      setEvents(prev => prev.map(ev => ev.id === updatedEvent.id ? updatedEvent : ev));
    } catch (err) {
      console.error('Error confirming event:', err);
    }
  };

  const formatUtc = (timestamp) => {
    if (!timestamp) return '';
    const d = new Date(Number(timestamp));
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
  };

  const currentEvents = useMemo(() =>
    events
      .filter(ev => !['Cancelled', 'Completed'].includes(ev.status))
      .map(ev => ({
        ...ev,
        periodDisplay: (ev.estimatedperiod?.length === 2)
          ? `${formatUtc(ev.estimatedperiod[0])} - ${formatUtc(ev.estimatedperiod[1])}`
          : '',
        locationsDisplay: ev.locations
          ? ev.locations.map(loc => loc.toUpperCase().replace(/FIR$/i, '') + 'FIR').join(', ')
          : '-'
      })),
    [events]
  );

  const archivedEvents = useMemo(() =>
    events
      .filter(ev => ['Cancelled', 'Completed'].includes(ev.status))
      .map(ev => ({
        ...ev,
        periodDisplay: (ev.estimatedperiod?.length === 2)
          ? `${formatUtc(ev.estimatedperiod[0])} - ${formatUtc(ev.estimatedperiod[1])}`
          : '',
        locationsDisplay: ev.locations
          ? ev.locations.map(loc => loc.toUpperCase().replace(/FIR$/i, '') + 'FIR').join(', ')
          : '-'
      })),
    [events]
  );

  const columns = useMemo(() => [
    { Header: '#', accessor: 'id' },
    { Header: 'Type', accessor: 'type' },
    {
      Header: 'Name', accessor: 'name',
      Cell: ({ row }) => (
        <Link to={`/events/${row.original.id}`} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', fontWeight: 'bold' }}>
          {row.original.name}
        </Link>
      )
    },
    { Header: 'Period (est)', accessor: 'periodDisplay' },
    { Header: 'Locations', accessor: 'locationsDisplay' },
    { Header: 'Status', accessor: 'status',
        Cell: ({ value }) => {
          const colorMap = { Planned: '#1976d2', Confirmed: '#2e7d32' };
          const bgColor = colorMap[value] || '#9c27b0';

          return (
            <span style={{ display: 'inline-block',
              padding: '2px 6px',
              background: bgColor,
              color: '#fff',
              fontWeight: 'bold',
              fontSize: '0.85em',
            }}>
              {value}
            </span>
          );
        }
    },
    { Header: 'Network Impact', accessor: 'impact' },
    {
        Header: 'Actions',
        id: 'actions',
        Cell: ({ row }) => {
          const { creator, locations, status, id } = row.original;
          const isCreator = creator?.toString() === user.cid.toString();
          const userHousekeeperLocations = user.housekeeper?.split(',') || [];
          const isHousekeeper = locations?.some(loc => userHousekeeperLocations.includes(loc));

          const canEdit = isCreator || isHousekeeper;
          const approvable = isHousekeeper && status === 'Planned';

          if (!canEdit) return null;

          return (
            <Box display="flex" gap={0}>
              <IconButton color='primary' size="small" onClick={() => { setEditingEvent(row.original); setFormOpen(true); }}>
                <EditCalendarIcon />
              </IconButton>
              {approvable && (
                <IconButton color="secondary" size="small" onClick={() => handleApprove(id)}>
                  <EventAvailableIcon />
                </IconButton>
              )}
              {isCreator && (
                <IconButton color="error" size="small" onClick={() => handleDelete(id)}>
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          );
        }
    }

  ], [user]);

  const archivedColumns = useMemo(() => [
    { Header: '#', accessor: 'id' },
    { Header: 'Type', accessor: 'type' },
    {
      Header: 'Name', accessor: 'name',
      Cell: ({ row }) => (
        <Link to={`/events/${row.original.id}`} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', fontWeight: 'bold' }}>
          {row.original.name}
        </Link>
      )
    },
    { Header: 'Period (est)', accessor: 'periodDisplay' },
    { Header: 'Locations', accessor: 'locationsDisplay' },
    { Header: 'Status', accessor: 'status' },
    { Header: 'Organisation', accessor: 'organisationname' },
  ], [user]);

  const currentTable = useTable(
    { columns, data: currentEvents, initialState: { sortBy: [{ id: 'id', desc: false }] } },
    useFilters,
    useSortBy
  );

  const archivedTable = useTable(
    { columns: archivedColumns, data: archivedEvents, initialState: { sortBy: [{ id: 'id', desc: false }] } },
    useFilters,
    useSortBy
  );

  const renderRows = ({ rows, prepareRow }) =>
    rows.length > 0 ? rows.map(row => {
      prepareRow(row);
      return (
        <tr key={row.id} {...row.getRowProps()}>
          {row.cells.map(cell => (
            <td key={cell.column.id} {...cell.getCellProps()}>{cell.render('Cell')}</td>
          ))}
        </tr>
      );
    }) : (
      <tr>
        <td colSpan={columns.length} style={{ textAlign: 'center', color: '#888' }}>
          No events found
        </td>
      </tr>
    );

    return (
      <>
      <Modal open={isOpen} onClose={onClose}>
        <Box style={ModalStyles.overlay}>
          <Box style={ModalStyles.modal}>
            <Box display="flex" justifyContent="space-between" alignItems="center" className="events-modal-title">
              <Box display="flex" alignItems="center">
                <img src='/images/globe.svg' alt='Events' style={{ width : '22px', height: 'auto', marginRight: '8px'}} />
                <Typography variant="h6" sx={{display: 'inline'}}>Network Events</Typography>
              </Box>
              <IconButton onClick={onClose}><CloseIcon /></IconButton>
            </Box>

            {loading ? (
              // 🔹 Show loader while fetching
              <Box display="flex" justifyContent="center" alignItems="center" height="64vh">
                Loading <img src="/images/ajax-loader.gif" alt="Loading..." style={{marginLeft: '10px', width: '20px'}} />
              </Box>
            ) : (
              <>
                <Box mb={2} mt={2}>
                  <Alert severity="info">
                    This list includes planned events and all Network Events confirmed by Cluster Housekeepers
                  </Alert>
                </Box>
                {/* Current Events */}
                <Box style={ModalStyles.sectionHeader}><div>Current Events</div></Box>
                <Box style={ModalStyles.sectionContent}>
                  <table className="list-table" style={{ marginBottom: 0 }} {...currentTable.getTableProps()}>
                    <thead>
                      {currentTable.headerGroups.map(hg => (
                        <tr key={hg.id} {...hg.getHeaderGroupProps()}>
                          {hg.headers.map(col => (
                            <th key={col.id} {...col.getHeaderProps(col.getSortByToggleProps())} style={{ cursor: 'pointer' }}>
                              {col.render('Header')}
                              {col.isSorted ? (col.isSortedDesc ? ' ▲' : ' ▼') : ''}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody {...currentTable.getTableBodyProps()}>
                      {renderRows(currentTable)}
                    </tbody>
                  </table>
                </Box>
                <Box display={'flex'} justifyContent={'end'}>
                    <Button variant="contained" sx={{ borderRadius: 0, mt: 1, mb: 2 }} onClick={() => { setEditingEvent(null); setFormOpen(true); }}>
                    Create Event
                    </Button>
                </Box>
                {/* Archived Events */}
                <Box style={ModalStyles.sectionHeader}><div>Archived Events</div></Box>
                <Box style={ModalStyles.sectionContent}>
                  <table className="list-table" style={{ marginBottom: 0 }} {...archivedTable.getTableProps()}>
                    <thead>
                      {archivedTable.headerGroups.map(hg => (
                        <tr key={hg.id} {...hg.getHeaderGroupProps()}>
                          {hg.headers.map(col => (
                            <th key={col.id} {...col.getHeaderProps(col.getSortByToggleProps())} style={{ cursor: 'pointer' }}>
                              {col.render('Header')}
                              {col.isSorted ? (col.isSortedDesc ? ' ▲' : ' ▼') : ''}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody {...archivedTable.getTableBodyProps()}>
                      {renderRows(archivedTable)}
                    </tbody>
                  </table>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Modal>
      {/* Create/Edit Event Form */}
      <EventForm user={user} open={formOpen} onClose={() => setFormOpen(false)} initialData={editingEvent} onSaved={() => fetchEvents()}  />
    </>
  );
};

export default Events;
